import { FunctionComponent, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import {
  RootState,
  useTypedDispatch,
  useTypedSelector,
} from "../../../app/store"
import { setActiveChoiceType } from "../decisionsSlice"
import { ActionChoiceProps, ChoiceType, ChooseActionProps } from "../types"

const ChooseAction: FunctionComponent<ChooseActionProps> = () => {
  const dispatch = useTypedDispatch()
  const location = useLocation()

  const selectedChoice = useTypedSelector((state: RootState) => {
    return state.decisionsSlice.activeChoiceType
  })

  // Check if user is in tutorial mode by checking URL path
  const isInTutorial = location.pathname.includes("/tutorial")

  // Safely access collaboration state with fallbacks
  const collaborationState = useTypedSelector((state: RootState) => {
    // Check if collaborationSlice exists in state
    if (state.collaborationSlice) {
      return {
        currentRoom: state.collaborationSlice.currentRoom,
        currentUserId: state.collaborationSlice.currentUserId,
        participants: state.collaborationSlice.participants
      }
    }
    return {
      currentRoom: null,
      currentUserId: null,
      participants: []
    }
  })

  const { currentRoom, participants } = collaborationState

  // Only show collaborative UI if:
  // 1. We have a room with multiple real participants (more than just current user)
  // 2. AND we're not in tutorial mode (detected via URL path)
  const isCollaborative = !isInTutorial &&
    currentRoom !== null &&
    Array.isArray(participants) &&
    participants.length > 1

  const handleSelectChoice = (choice: ChoiceType) => {
    dispatch(setActiveChoiceType(choice))
  }

  // const getChoiceTypeDisplayName = (choice: ChoiceType): string => {
  //   switch (choice) {
  //     case ChoiceType.decision:
  //       return "Make a Decision"
  //     case ChoiceType.plan:
  //       return "Create a Plan"
  //     case ChoiceType.search:
  //       return "Search for Information"
  //     default:
  //       return "Unknown Action"
  //   }
  // }

  // Simple collaboration rule: Only information searches are individual
  const canPerformIndividually = (choiceType: ChoiceType): boolean => {
    return choiceType === ChoiceType.search
  }

  const requiresVoting = (choiceType: ChoiceType): boolean => {
    return choiceType !== ChoiceType.search
  }

  return (
    <div className="self-stretch flex flex-col items-start justify-start">
      <div className="self-stretch flex flex-col items-start justify-start gap-[8px]">
        <div className="self-stretch flex flex-col items-start justify-start">
          <div className="relative leading-[24px] font-medium">
            {isCollaborative ? "I want to propose..." : "I want to..."}
          </div>
          {isCollaborative && (
            <div className="text-sm text-gray-600 mt-1">
              Your proposals will be voted on by the team
            </div>
          )}
        </div>
        <div className="self-stretch flex flex-row items-start justify-start gap-[12px] text-primary-800">
          <ActionChoice
            id="makeDecision"
            name="Make a Decision"
            keyName={ChoiceType.decision}
            icon="/v3/assets/simulation_decision_icon.svg"
            onSelectChoice={handleSelectChoice}
            helpText={isCollaborative 
              ? requiresVoting(ChoiceType.decision)
                ? "🗳️ Strategic decisions require team voting. Click to propose to your team."
                : "✅ You can make this decision individually."
              : "Making decisions is how you interact with the simulation world. Click here to make a decision."
            }
            isSelected={selectedChoice === ChoiceType.decision}
            isCollaborative={isCollaborative}
            canPerformIndividually={isCollaborative ? canPerformIndividually(ChoiceType.decision) : true}
          />
          <ActionChoice
            id="createPlan"
            name="Create a Plan"
            keyName={ChoiceType.plan}
            icon="/v3/assets/simulation_plan_icon.svg"
            onSelectChoice={handleSelectChoice}
            helpText={isCollaborative
              ? requiresVoting(ChoiceType.plan)
                ? "🗳️ Action plans require team voting. Click to propose to your team."
                : "✅ You can create this plan individually and share with the team."
              : "Create plans for any future decisions you may want to make. Click here to make a plan."
            }
            isSelected={selectedChoice === ChoiceType.plan}
            isCollaborative={isCollaborative}
            canPerformIndividually={isCollaborative ? canPerformIndividually(ChoiceType.plan) : true}
          />
          <ActionChoice
            id="searchForInformation"
            name="Search for Information"
            keyName={ChoiceType.search}
            icon="/v3/assets/simulation_search_icon.svg"
            onSelectChoice={handleSelectChoice}
            helpText={isCollaborative
              ? canPerformIndividually(ChoiceType.search)
                ? "🔍 Information search is private to you. Results will only be visible to you unless you choose to share them."
                : "🗳️ Information search requires team approval. Click to propose to your team."
              : "If you need more information about anything within the simulation world, this is where you can find it. Click here to search for information you may want to know."
            }
            isSelected={selectedChoice === ChoiceType.search}
            isCollaborative={isCollaborative}
            canPerformIndividually={isCollaborative ? canPerformIndividually(ChoiceType.search) : true}
          />
        </div>
      </div>
    </div>
  )
}

const ActionChoice: FunctionComponent<ActionChoiceProps & { isCollaborative?: boolean; canPerformIndividually?: boolean }> = (props) => {
  const [boxClasses, setBoxClasses] = useState("")

  const handleClick = () => {
    props.onSelectChoice(props.keyName)
  }

  useEffect(() => {
    const isSelectedClasses = props.isSelected
      ? "bg-blue-light-200 border-blue-light-600"
      : "bg-base-white border-gray-200"
    
    // Add visual indicator for individual vs voting actions
    const permissionClasses = props.isCollaborative && props.canPerformIndividually
      ? "ring-2 ring-green-300" // Green ring for individual actions
      : props.isCollaborative && props.canPerformIndividually === false
      ? "ring-2 ring-orange-300" // Orange ring for voting required
      : ""
    
    let classes =
      "flex-1 h-full rounded-xl box-border flex flex-col items-start cursor-pointer hover:bg-blue-light-200 select-none"
    classes += ` justify-start p-4 border-[1px] border-solid ${isSelectedClasses} ${permissionClasses}`
    setBoxClasses(classes)
  }, [props.isSelected, props.isCollaborative, props.canPerformIndividually])

  return (
    <div
      id={props.id}
      className={boxClasses}
      onClick={handleClick}
      role="pseudo-button">
      <div className="self-stretch flex flex-col items-start justify-start gap-[8px]">
        <div className="self-stretch flex flex-row items-start justify-between">
          <img
            className="relative rounded-2xl w-8 h-8 mix-blend-multiply"
            alt=""
            src={props.icon}
          />
          <img
            className="relative w-5 h-5 overflow-hidden shrink-0 cursor-help"
            alt=""
            src="/v3/assets/help-circle.svg"
            title={props.helpText}
          />
        </div>
        <div className="self-stretch flex flex-col items-start justify-start">
          <div className="relative leading-[24px] font-medium">
            {props.isCollaborative && props.canPerformIndividually === false 
              ? `Propose: ${props.name}` 
              : props.name}
          </div>
        </div>
        {props.isCollaborative && (
          <div className="text-xs text-gray-600 mt-1">
            {props.canPerformIndividually ? (
              <span className="text-green-600 font-medium">✅ Individual action</span>
            ) : (
              <span className="text-orange-600 font-medium">🗳️ Requires team vote</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ChooseAction

